<template>
    <v-expand-transition>
        <v-data-table
            v-if="groupItems"
            :headers="headers"
            :items="groupItems"
            :loading="detailsLoading"
            :items-per-page="-1"
            :mobile-breakpoint="0"
            hide-default-footer
            :hide-default-header="isMobile"
            :group-by="[{key: 'date'}]"
            disable-sort
            class="elevation-1"
        >
            <template v-slot:group-header="{item, isGroupOpen, toggleGroup, columns}">
                <tr>
                    <td
                        :ref="
                            el => {
                                if (needToExpandGroups && !isGroupOpen(item)) {
                                    toggleGroup(item)
                                }
                            }
                        "
                        :colspan="columns.length"
                        style="background-color: rgb(238, 238, 238)"
                        class="pa-4"
                        @click="toggleGroup(item)"
                    >
                        <div :ref="item.value" class="d-flex justify-space-between">
                            <div>
                                <v-icon start>{{ isGroupOpen(item) ? 'mdi-chevron-down' : 'mdi-chevron-up' }} </v-icon>
                                <span class="font-weight-bold text-caption">
                                    {{ $filters.dateShortFormat(item.value) }}
                                </span>
                            </div>
                            <div v-if="isMobile" class="py=2">
                                <tour-included-entries :entries="itemsAdapter(item.items.map(i => i.raw))" />
                            </div>
                        </div>
                    </td>
                </tr>
            </template>
            <template v-slot:item.groupId="{item}">
                <span>{{ item.groupId }}</span>
            </template>
            <template v-slot:item.service="{item}">
                <div class="d-md-flex flex-md-column py-3 align-md-start">
                    <div class="font-weight-medium">
                        <v-icon size="small" start class="d-inline-block">
                            {{ getServiceTypeIcon(item) }}
                        </v-icon>
                        <span class="text-subtitle-2" v-html="item.serviceName" />
                    </div>
                    <span class="text-caption text--secondary">
                        {{ `${item.city}, ${item.country}` }}
                    </span>
                </div>
            </template>
            <template v-slot:item.status="{item}">
                <span :class="item.statusColor">{{ $t(`extranetGroupStatus.${item.status}`) }}</span>
            </template>
            <template v-slot:item.programType="{item}">
                {{ $t(`${item.programType.toLowerCase()}`) }}
            </template>
            <template v-slot:item.details="{item}">
                <div class="d-flex flex-column align-end py-3">
                    <span v-if="isMobile" :class="item.statusColor" class="pb-1">
                        {{ $t(`extranetGroupStatus.${item.status}`) }}
                    </span>
                    <v-btn :color="'primary'" size="small" :disabled="detailsLoading" @click="showDetails(item)">
                        {{ $t('details') }}
                    </v-btn>
                </div>
            </template>
            <template v-slot:item.mobile="{item}">
                <div class="py-3 d-flex justify-space-between">
                    <div class="flex-grow-1">
                        <div class="font-weight-medium">
                            <v-icon size="small" start class="d-inline-block">
                                {{ getServiceTypeIcon(item) }}
                            </v-icon>
                            <span class="text-subtitle-2" v-html="item.serviceName" />
                        </div>
                        <div class="text-caption text--secondary">
                            {{ `${item.city}, ${item.country}` }}
                        </div>
                        <div class="text-caption">
                            {{
                                `${$filters.hoursFromDate(item.dateTime)}, ${$t(item.programType.toLowerCase())}, ${
                                    item.touristsQuantity
                                } ${$t('guest', item.touristsQuantity)}`
                            }}
                        </div>
                    </div>
                    <div class="flex-grow-0 text-right">
                        <div class="text-subtitle-2">{{ item.groupId }}</div>
                        <div :class="item.statusColor" class="mb-2">
                            {{ $t(`extranetGroupStatus.${item.status}`) }}
                        </div>
                        <v-btn :color="'primary'" size="small" :disabled="detailsLoading" @click="showDetails(item)">
                            {{ $t('details') }}
                        </v-btn>
                    </div>
                </div>
            </template>
        </v-data-table>
    </v-expand-transition>
</template>

<script lang="ts">
    import {Component, Prop, VModel, Vue, Watch} from 'vue-facing-decorator'
    import {getServiceTypeIcon} from '~src/utils/helpers'
    import TourIncludedEntries from '~src/components/tours/snippets/TourIncludedEntries.vue'

    @Component({
        components: {TourIncludedEntries},
    })
    export class GroupsListTable extends Vue {
        @VModel() selectedGroup
        @Prop() groupItems
        @Prop() detailsLoading

        needToExpandGroups = false

        @Watch('groupItems')
        async onGroupItemsChange() {
            //костыль, чтобы по дефолту были открыты группы в таблице
            this.needToExpandGroups = true
            await this.$nextTick()
            this.needToExpandGroups = false
        }
        getDayOfWeek(dateString) {
            const date = new Date(dateString)

            return new Intl.DateTimeFormat(this.$i18n.locale, {weekday: 'short'}).format(date)
        }

        getServiceTypeIcon(service) {
            return getServiceTypeIcon(service)
        }

        showDetails(group) {
            this.selectedGroup = group
            this.$emit('show-details')
        }

        itemsAdapter(items) {
            return items.map(item => ({type: item.serviceType}))
        }

        get headers() {
            if (this.isMobile) return [{value: 'mobile'}]

            return [
                {title: 'Id', value: 'groupId', align: 'center'},
                {title: this.$t('time'), value: 'time'},
                {title: this.$t('status'), value: 'status'},
                {title: this.$t('order_service'), value: 'service'},
                {title: this.$t('guests'), value: 'touristsQuantity', align: 'center'},
                {title: this.$t('type'), value: 'programType'},
                {title: this.$t('roles.guide'), value: 'guideName'},
                {title: this.$t('roles.driver'), value: 'driverName'},
                {title: this.$t('vehicle'), value: 'vehicle'},
                {title: this.$t('details'), value: 'details', align: 'center'},
            ]
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }
    }

    export default GroupsListTable
</script>

<style scoped lang="scss">
    :deep(.v-data-table__tr) {
        & {
            .v-data-table__td:first-of-type {
                display: none;
            }
        }
    }
    :deep(thead) {
        & {
            .v-data-table__td:first-of-type {
                display: none;
            }
        }
    }
</style>
